import React, { useCallback, useEffect, useState } from 'react';
import { useQueryParams } from '../admin/useQueryParams';
import { errorMessage } from '../admin/adminApi';
import { formatDate, formatDateTime } from '../admin/format';
import { OFFICE_LABELS } from './offices';
import StepTrail, { originLabel } from '../approvals/StepTrail';
import { formatAmount } from '../approvals/approvalTypes';
import InvoiceChecks from '../approvals/InvoiceChecks';
import { downloadFile } from '../documents/documentUtils';
import {
  Badge, DataTable, Drawer, ListLayout, Panel, SegmentedControl, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass
} from '../admin/AdminUI';

export const decisionTitle = (d) => (d.type === 'invoice'
  ? `${d.payload.vendorName || 'Invoice'} · ${formatAmount(d.payload.total)}`
  : d.payload.title);

const shortDate = (value) => (value ? formatDate(value) : '—');

function InvoiceDetails({ decision, api, slug }) {
  const p = decision.payload;
  const download = () => downloadFile(() => api.download(`/${slug}/decisions/${decision._id}/file`), p.file.name)
    .catch(() => alert('Failed to download the invoice'));
  return (
    <section className="mt-4 space-y-3">
      <p className="text-sm text-foreground">
        {p.invoiceNumber ? `Invoice ${p.invoiceNumber} · ` : ''}{shortDate(p.invoiceDate)} · due {shortDate(p.dueDate)}
        <span className="block text-muted-foreground">{formatAmount(p.subtotal)} + GST {formatAmount(p.gst)} = {formatAmount(p.total)}</span>
      </p>
      {p.description && <p className="text-sm text-foreground">{p.description}</p>}
      {p.file && <button type="button" onClick={download} className={btnSecondary}>Download {p.file.name}</button>}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-1">Checks</h3>
        <InvoiceChecks checks={p.checks} />
      </div>
    </section>
  );
}

function QuestionForm({ onSubmit, busy }) {
  const [text, setText] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(text).then((ok) => ok && setText('')); }} className="mt-3 space-y-2">
      <label htmlFor="decision-note" className={labelClass}>Ask a question or add a comment</label>
      <textarea id="decision-note" rows={2} value={text} onChange={(e) => setText(e.target.value)} className={inputClass} />
      <button type="submit" disabled={!!busy || !text.trim()} className={btnSecondary}>Add</button>
    </form>
  );
}

const STATUS_LABELS = { approved: 'Approved', rejected: 'Rejected', returned: 'Sent back', pending: 'Waiting' };

export function DecisionStatus({ decision }) {
  if (decision.waiting) {
    return <Badge className="text-primary border-primary normal-case">{decision.tally.approvals} of {decision.tally.needed} approvals</Badge>;
  }
  const className = decision.status === 'approved' ? 'text-success border-success' : decision.status === 'returned' ? 'text-warning border-warning' : 'text-destructive border-destructive';
  return <Badge className={className}>{STATUS_LABELS[decision.status] || decision.status}</Badge>;
}

function NewDecisionForm({ api, slug, onSaved, onCancel }) {
  const [form, setForm] = useState({ title: '', description: '', amount: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post(`/${slug}/decisions`, { ...form, amount: form.amount === '' ? null : Number(form.amount) });
      onSaved(response.data.decision);
    } catch (err) {
      alert(errorMessage(err, 'Failed to create the decision'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="new-decision-title" className={labelClass}>What should council decide? *</label>
        <input id="new-decision-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="e.g., Buy 6 bike racks for P1" />
      </div>
      <div>
        <label htmlFor="new-decision-description" className={labelClass}>Details</label>
        <textarea id="new-decision-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label htmlFor="new-decision-amount" className={labelClass}>Amount (optional)</label>
        <input id="new-decision-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
      </div>
      <p className="text-xs text-muted-foreground">Council's own decisions go straight to a council vote, without the Admin step.</p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Put to council'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

export function DecisionDetail({ decision, api, slug, canAct, onChanged, onClose }) {
  const [busy, setBusy] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [sendingBack, setSendingBack] = useState(false);
  const council = decision.steps.find((s) => s.kind === 'council');

  const act = async (action, path, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action);
    try {
      const response = await api.post(`/${slug}/decisions/${decision._id}/${path}`, body);
      onChanged(response.data.decision);
      setSendingBack(false);
      return true;
    } catch (err) {
      alert(errorMessage(err, 'That did not work'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer
      title={decisionTitle(decision)}
      badges={<><DecisionStatus decision={decision} />{decision.version > 1 && <Badge>Version {decision.version}</Badge>}{decision.test && <Badge className="text-warning border-warning">Test</Badge>}</>}
      subtitle={`${originLabel(decision.origin) || 'Proposed'} · ${formatDateTime(decision.createdAt)}`}
      onClose={onClose}
    >
      <StepTrail approval={decision} />
      {decision.type === 'invoice' && <InvoiceDetails decision={decision} api={api} slug={slug} />}
      {decision.type !== 'invoice' && decision.payload.amount !== null && decision.payload.amount !== undefined && (
        <p className="mt-4 text-sm text-foreground"><span className="text-muted-foreground">Amount:</span> {formatAmount(decision.payload.amount)}</p>
      )}
      {decision.type !== 'invoice' && decision.payload.description && <p className="mt-3 text-sm text-foreground whitespace-pre-wrap">{decision.payload.description}</p>}
      {(decision.notes || []).length > 0 && (
        <section className="mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-1">Questions and comments</h3>
          <ul className="space-y-1 text-sm">
            {decision.notes.map((note, i) => (
              <li key={i}><span className="text-foreground">{note.byName}</span><span className="text-muted-foreground"> · {formatDate(note.at)}{note.test ? ' · test' : ''}:</span> {note.text}</li>
            ))}
          </ul>
        </section>
      )}
      {decision.status === 'returned' && decision.feedback && (
        <p className="mt-3 text-sm text-warning">Sent back{decision.feedback.byName ? ` by ${decision.feedback.byName}` : ''}: {decision.feedback.text}</p>
      )}

      <section className="mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-2">Votes</h3>
        {decision.tally && (
          <p className="text-xs text-muted-foreground mb-2">
            Needs {decision.tally.needed} of {decision.tally.eligible} council members{decision.tally.office ? `, including the ${OFFICE_LABELS[decision.tally.office]}` : ''}.
          </p>
        )}
        {(council.votes || []).length === 0 ? <p className="text-sm text-muted-foreground">No votes yet.</p> : (
          <ul className="space-y-1 text-sm">
            {council.votes.map((v) => (
              <li key={v.userId} className="flex gap-2">
                <span className={v.vote === 'approve' ? 'text-success' : 'text-destructive'}>{v.vote === 'approve' ? '✓' : '✕'}</span>
                <span className="text-foreground">{v.name || 'Council member'}{v.office ? `, ${OFFICE_LABELS[v.office]}` : ''}</span>
                <span className="text-muted-foreground">· {formatDate(v.at)}{v.test ? ' · test' : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {decision.waiting && (
        <section className="mt-6 pt-4 border-t border-border">
          {!canAct ? (
            <p className="text-sm text-muted-foreground">Pick who you are acting as to vote.</p>
          ) : decision.myConflict ? (
            <p className="text-sm text-muted-foreground">You declared a conflict of interest, so you don't vote on this.</p>
          ) : (
            <>
              {decision.myVote && <p className="text-sm text-muted-foreground mb-2">You voted to {decision.myVote}. You can change your vote until it's decided.</p>}
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={!!busy} onClick={() => act('approve', 'vote', { vote: 'approve' })} className={btnPrimary}>{busy === 'approve' ? 'Saving...' : 'Approve'}</button>
                <button type="button" disabled={!!busy} onClick={() => act('reject', 'vote', { vote: 'reject' })} className={btnDanger}>{busy === 'reject' ? 'Saving...' : 'Reject'}</button>
                {decision.origin?.kind === 'agent' && (
                  <button type="button" disabled={!!busy} onClick={() => setSendingBack(!sendingBack)} className={btnSecondary}>Send back with feedback</button>
                )}
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => act('conflict', 'conflict', {}, 'Declare a conflict of interest? Your vote is removed and you leave this decision.')}
                  className={btnSecondary}
                >
                  Declare a conflict
                </button>
              </div>
              <QuestionForm busy={busy} onSubmit={(text) => act('note', 'note', { text })} />
              {sendingBack && (
                <form onSubmit={(e) => { e.preventDefault(); act('send-back', 'send-back', { feedback }); }} className="mt-3 space-y-2">
                  <label htmlFor="send-back-feedback" className={labelClass}>What should the agent change?</label>
                  <textarea id="send-back-feedback" required rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} className={inputClass} />
                  <button type="submit" disabled={!!busy || !feedback.trim()} className={btnPrimary}>Send back</button>
                </form>
              )}
            </>
          )}
        </section>
      )}
    </Drawer>
  );
}

function DecisionsTab({ slug, info, api }) {
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);
  const status = params.get('status') === 'decided' ? 'decided' : 'waiting';
  const selectedId = params.get('decision');
  const creating = params.get('new') === '1';
  const canAct = !!info.membership;

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/${slug}/decisions`, { status });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to load decisions'));
    }
  }, [api, slug, status]);

  useEffect(() => { load(); }, [load]);

  const selected = data?.decisions.find((d) => d._id === selectedId);

  const columns = [
    { key: 'title', header: 'Decision', className: 'font-medium text-foreground', render: decisionTitle },
    { key: 'from', header: 'From', hideBelow: 'md', className: 'text-muted-foreground', render: (d) => originLabel(d.origin) || '—' },
    { key: 'amount', header: 'Amount', hideBelow: 'lg', render: (d) => { const amount = d.type === 'invoice' ? d.payload.total : d.payload.amount; return amount !== null && amount !== undefined ? formatAmount(amount) : '—'; } },
    { key: 'status', header: 'Status', render: (d) => <DecisionStatus decision={d} /> },
    { key: 'createdAt', header: 'Proposed', hideBelow: 'md', className: 'whitespace-nowrap text-muted-foreground', render: (d) => formatDate(d.createdAt) }
  ];

  const drawer = creating ? (
    <Drawer title="New decision" onClose={() => setParams({ new: null })}>
      {canAct ? (
        <NewDecisionForm api={api} slug={slug} onCancel={() => setParams({ new: null })} onSaved={(d) => { setParams({ new: null, status: null, decision: d._id }); load(); }} />
      ) : <p className="text-sm text-muted-foreground">Pick who you are acting as first.</p>}
    </Drawer>
  ) : selected ? (
    <DecisionDetail key={selected._id} decision={selected} api={api} slug={slug} canAct={canAct} onChanged={() => load()} onClose={() => setParams({ decision: null })} />
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Decisions</h2>
          <SegmentedControl
            label="Decision status"
            value={status}
            onChange={(value) => setParams({ status: value === 'waiting' ? null : value, decision: null })}
            options={[
              { value: 'waiting', label: 'Waiting', count: data?.counts.waiting },
              { value: 'decided', label: 'Decided', count: data?.counts.decided }
            ]}
          />
          <button type="button" onClick={() => setParams({ new: '1', decision: null })} className={`${btnPrimary} ml-auto`}>+ New decision</button>
        </Toolbar>
        {!data ? <Spinner /> : (
          <DataTable
            label="Decisions"
            columns={columns}
            rows={data.decisions}
            selectedId={selectedId}
            onRowClick={(d) => setParams({ decision: d._id, new: null })}
            emptyMessage={status === 'waiting' ? 'Nothing is waiting for council.' : 'No decisions yet.'}
          />
        )}
      </Panel>
    </ListLayout>
  );
}

export default DecisionsTab;
